/**
 * WebSocket hook for voice note push notifications from NotificationHub DO.
 * Ported from mina4's notification_service.dart.
 */
import { useEffect, useRef } from "react";
import { NOTIFICATION_WS_URL } from "../config/constants";

const LOG = "[Galena] NotificationHub";

export interface VoiceNoteNotification {
  type: "new_voice_note";
  noteId: string;
  sender: string;
}

export function useNotificationHub(
  onNotification: (msg: VoiceNoteNotification) => void
) {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectDelayRef = useRef(3000);
  const disposedRef = useRef(false);

  const callbackRef = useRef(onNotification);
  callbackRef.current = onNotification;

  useEffect(() => {
    // Skip notification hub on localhost — worker doesn't support WebSocket locally
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      console.log('[Galena] NotificationHub skipped on localhost');
      return;
    }

    disposedRef.current = false;

    function connect() {
      if (disposedRef.current) return;

      console.log(`${LOG} Connecting to ${NOTIFICATION_WS_URL}`);
      const ws = new WebSocket(NOTIFICATION_WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log(`${LOG} Connected`);
        reconnectDelayRef.current = 3000;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log(`${LOG} Message received:`, data);
          if (data.type === "new_voice_note") {
            callbackRef.current(data as VoiceNoteNotification);
          }
        } catch (e) {
          console.warn(`${LOG} Failed to parse message:`, event.data, e);
        }
      };

      ws.onclose = (event) => {
        console.log(`${LOG} Connection closed — code=${event.code}, reason="${event.reason}", wasClean=${event.wasClean}`);
        if (!disposedRef.current) {
          scheduleReconnect();
        }
      };

      ws.onerror = (event) => {
        console.error(`${LOG} Connection error:`, event);
        ws.close();
      };
    }

    function scheduleReconnect() {
      const delay = reconnectDelayRef.current;
      console.log(`${LOG} Scheduling reconnect in ${delay}ms`);
      setTimeout(() => {
        if (!disposedRef.current) connect();
      }, delay);
      reconnectDelayRef.current = Math.min(
        reconnectDelayRef.current * 1.5,
        30000
      );
    }

    connect();

    return () => {
      console.log(`${LOG} Disposing — closing WebSocket`);
      disposedRef.current = true;
      wsRef.current?.close();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
